import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';

import { useIsLoggedInQuery } from '../../../../redux/apis/authApi';
import { useGetHRXAttendanceOverviewQuery } from '../../../../redux/apis/attendanceApi';
import { selectDisplayTimezone } from '../../../../redux/utils/apiLocality';

import { Skeleton } from '../../../../components/UI/Skeleton/Skeleton';
import { formatterTime } from '../../../../utils/handler';
import { formatTimestampInTimezone } from '../../../../utils/timedoctorAttendanceHelpers';
import { getHrxDisplayCheckOutTime } from '../../../../utils/attendanceTimelineHelpers';

import {
  BsCheckCircleFill,
  BsClock,
  BsClockFill,
  BsClockHistory,
  BsExclamationTriangleFill,
  BsPauseFill,
  BsPersonDashFill
} from 'react-icons/bs';
import { ROLES } from '../../../../utils/constant/global';

const toneForCurrent = (value) => {
  const key = String(value || '')
    .trim()
    .toLowerCase();
  if (!key || key === 'n/a' || key === 'na') return 'muted';
  if (key.includes('checked in')) return 'ok';
  if (key.includes('break')) return 'warn';
  if (key.includes('checked out')) return 'muted';
  return 'bad';
};

const toneForLate = (value) => {
  const key = String(value || '')
    .trim()
    .toLowerCase();
  if (!key || key === 'n/a' || key === 'na') return 'muted';
  if (key.includes('late')) return 'warn';
  return 'ok';
};

const toneForAbsence = (value) => {
  const key = String(value || '')
    .trim()
    .toLowerCase();
  if (!key || key === 'n/a' || key === 'na') return 'muted';
  if (key === 'wfh' || key.includes('remote') || key.includes('work from home')) return 'brand';
  if (key === 'present') return 'ok';
  if (key.includes('absent')) return 'bad';
  return 'info';
};

const AttendanceOverview = ({ userId }) => {
  const { data: isLoggedInData } = useIsLoggedInQuery();
  const timeZone = useSelector(selectDisplayTimezone) || 'Asia/Kolkata';

  const isAdminOrHr = [ROLES.ADMIN, ROLES.HR].includes(isLoggedInData?.data?.hrxRole?.roleName);
  const {
    data: overviewData,
    isLoading,
    isFetching,
    refetch: attendanceOverviewFetch
  } = useGetHRXAttendanceOverviewQuery({
    userId: isAdminOrHr && userId ? userId : isLoggedInData?.data?._id
  });

  useEffect(() => {
    attendanceOverviewFetch();
  }, []);

  const {
    checkInTime,
    totalWorkMilliseconds,
    totalBreakMilliseconds,
    // userName,
    currentStatus,
    lateStatus,
    absenceAlert
  } = overviewData?.data || {};

  const displayCheckOutTime = getHrxDisplayCheckOutTime(overviewData?.data);

  const displayAbsenceAlert = (() => {
    const alert = String(absenceAlert || '')
      .trim()
      .toUpperCase();
    if (
      alert === 'WFH' ||
      alert === 'REMOTE' ||
      alert.includes('WORK FROM HOME') ||
      alert.includes('WFH')
    ) {
      return 'WFH';
    }
    return absenceAlert;
  })();

  if (isLoading || isFetching) {
    return (
      <div className="hrx-ov" role="status" aria-live="polite" aria-busy="true">
        <span className="visually-hidden">Loading…</span>
        {[2, 2, 3].map((rows, cardIndex) => (
          <section className="hrx-ov-card" key={cardIndex}>
            <div className="hrx-ov-list">
              {Array.from({ length: rows }).map((_, row) => (
                <div className="hrx-ov-row" key={row}>
                  <Skeleton variant="circle" width={28} height={28} />
                  <Skeleton variant="text" width={cardIndex === 2 ? '62%' : '48%'} height={12} />
                  <Skeleton variant="text" width={46} height={12} />
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>
    );
  }

  if (!overviewData) {
    return (
      <div className="text-center mt-4">
        <h5>No attendance data available</h5>
      </div>
    );
  }

  const checkInLabel = checkInTime ? formatTimestampInTimezone(checkInTime, timeZone) : 'N/A';
  const checkOutLabel = displayCheckOutTime
    ? formatTimestampInTimezone(displayCheckOutTime, timeZone)
    : 'N/A';
  const workLabel = totalWorkMilliseconds ? formatterTime(totalWorkMilliseconds / 1000) : 'N/A';
  const breakLabel = totalBreakMilliseconds ? formatterTime(totalBreakMilliseconds / 1000) : 'N/A';

  const currentTone = toneForCurrent(currentStatus);
  const lateTone = toneForLate(lateStatus);
  const absenceTone = toneForAbsence(displayAbsenceAlert);

  return (
    <div className="hrx-ov">
      <section className="hrx-ov-card">
        <div className="hrx-ov-list">
          <div className="hrx-ov-row hrx-ov-metric--in">
            <span className="hrx-ov-icon">
              <BsClock />
            </span>
            <span className="hrx-ov-kicker">Check-In</span>
            <strong className={checkInLabel === 'N/A' ? 'is-empty' : ''}>{checkInLabel}</strong>
          </div>
          <div className="hrx-ov-row hrx-ov-metric--out">
            <span className="hrx-ov-icon">
              <BsClockHistory />
            </span>
            <span className="hrx-ov-kicker">Check-Out</span>
            <strong className={checkOutLabel === 'N/A' ? 'is-empty' : ''}>{checkOutLabel}</strong>
          </div>
        </div>
      </section>

      <section className="hrx-ov-card">
        <div className="hrx-ov-list">
          <div className="hrx-ov-row hrx-ov-metric--work">
            <span className="hrx-ov-icon">
              <BsClockFill />
            </span>
            <span className="hrx-ov-kicker">Work Duration</span>
            <strong className={workLabel === 'N/A' ? 'is-empty' : ''}>{workLabel}</strong>
          </div>
          <div className="hrx-ov-row hrx-ov-metric--break">
            <span className="hrx-ov-icon">
              <BsPauseFill />
            </span>
            <span className="hrx-ov-kicker">Break Duration</span>
            <strong className={breakLabel === 'N/A' ? 'is-empty' : ''}>{breakLabel}</strong>
          </div>
        </div>
      </section>

      <section className="hrx-ov-card">
        <div className="hrx-ov-list">
          <div className={`hrx-ov-row hrx-ov-metric--${currentTone}`}>
            <span className="hrx-ov-icon">
              <BsCheckCircleFill />
            </span>
            <span className="hrx-ov-kicker">Current Status</span>
            <strong className={currentStatus ? '' : 'is-empty'}>{currentStatus || 'N/A'}</strong>
          </div>
          <div className={`hrx-ov-row hrx-ov-metric--${lateTone}`}>
            <span className="hrx-ov-icon">
              <BsExclamationTriangleFill />
            </span>
            <span className="hrx-ov-kicker">Late Status</span>
            <strong className={lateStatus ? '' : 'is-empty'}>{lateStatus || 'N/A'}</strong>
          </div>
          <div className={`hrx-ov-row hrx-ov-metric--${absenceTone}`}>
            <span className="hrx-ov-icon">
              <BsPersonDashFill />
            </span>
            <span className="hrx-ov-kicker">Absence Alert</span>
            <strong className={displayAbsenceAlert ? '' : 'is-empty'}>
              {displayAbsenceAlert || 'N/A'}
            </strong>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AttendanceOverview;
